import { OptionBase, OptionGrouped } from '../../components/common/mygame/combobox/combobox'
import { ActionMeta, MultiValue, Options, SingleValue } from 'react-select'
import i18next from 'i18next'

export const SELECT_ALL_ID = -99

export type SelectAllOption = {
  id: number
  label: string
}

/**
 * Represents an option that allows selecting all items in a combobox.
 * Use for multi-store and multi-sport selectors.
 */
export const selectAllOption = (): SelectAllOption => ({
  id: SELECT_ALL_ID,
  label: i18next.t('select.select_all')
})

export const normalizeIds = (ids: (string | number)[]) => {
  if (ids.every(id => typeof id === 'number')) {
    return ids as number[]
  }
  if (ids.every(id => typeof id === 'string')) {
    return ids as string[]
  }
  return ids.map(id => id.toString())
}

export const isOptionSelected = <T extends OptionBase>(option: T, selectedValue: Options<T>) =>
  selectedValue.some(selected => selected.id === option.id)

export const allSelectableOptionsAreSelected = <T extends OptionBase>(options, valueSelected) => {
  const optionsSelectable =
    'options' in options[0]
      ? (options as OptionGrouped<T>[])
          .flatMap(group => group.options)
          .filter(option => option.id !== SELECT_ALL_ID && !option.isDisabled)
      : (options as T[]).filter(option => option.id !== SELECT_ALL_ID && !option.isDisabled)

  const optionsSelected = valueSelected.filter(val => val.id !== SELECT_ALL_ID)

  return optionsSelectable.length === optionsSelected.length
}

export const processComboboxChange = <T extends OptionBase>(
  value: SingleValue<T> | MultiValue<T>,
  actionMeta: ActionMeta<T>,
  onChange: (value: number | string | null | number[] | string[] | []) => void,
  data: T[],
  disabledItemsIds: number[] | string[]
) => {
  const isMultiMode = Array.isArray(value)

  if (isMultiMode) {
    multiValueMode(value as MultiValue<T>, actionMeta, onChange, data, disabledItemsIds)
  } else {
    singleValueMode(value as SingleValue<T>, actionMeta, onChange)
  }
}

export const singleValueMode = <T extends OptionBase>(
  value: SingleValue<T>,
  actionMeta: ActionMeta<T>,
  onChange: (value: number | string | null) => void
) => {
  onChange(actionMeta.action === 'clear' ? null : value.id)
}

export const multiValueMode = <T extends OptionBase>(
  value: MultiValue<T>,
  actionMeta: ActionMeta<T | SelectAllOption>,
  onChange: (value: string[] | number[]) => void,
  data: (T | SelectAllOption)[],
  disabledItemsIds: number[] | string[]
) => {
  const isDeselectAll = actionMeta.action === 'deselect-option' && actionMeta.option.id === SELECT_ALL_ID

  if (actionMeta.action === 'clear' || isDeselectAll) {
    onChange([])
    return
  }

  if (actionMeta.action === 'select-option' && actionMeta.option?.id === SELECT_ALL_ID) {
    const dataWithoutDisabledOptions = data.filter(
      option => !disabledItemsIds?.some(id => id.toString() === option.id.toString())
    )
    onChange(normalizeIds(dataWithoutDisabledOptions.map(option => option.id)))
    return
  }

  if (value === null || value.length === 0) {
    onChange([])
    return
  }

  onChange(normalizeIds(value.map(option => option.id)))
}
